import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Button, Row, Col, Card, ListGroup, Badge, Alert } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { useProfileMutation } from "../slices/usersApiSlice";
import { setCredentials } from "../slices/authSlice";
import { useGetMyOrdersQuery } from "../slices/ordersApiSlice";

const ProfileScreen = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    const dispatch = useDispatch();
    const { userInfo } = useSelector(state => state.auth);

    const [updateProfile, { isLoading: loadingUpdateProfile }] = useProfileMutation();
    const { data: orders, isLoading, error: errorOrders } = useGetMyOrdersQuery();

    useEffect(() => {
        if(userInfo) {
            setName(userInfo.name);
            setEmail(userInfo.email);
        }
    }, [userInfo]);

    const submitHandler = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        try {
            const res = await updateProfile({
                _id: userInfo._id,
                name,
                email,
                password
            }).unwrap();
            dispatch(setCredentials(res));
            setSuccess('Profile updated successfully!');
            setPassword('');
            setConfirmPassword('');
        } catch (err) {
            setError(err?.data?.message || err.error || 'Failed to update profile');
        }
    };

    return (
        <>
            <h1 className="mb-4">
                <i className="fas fa-user-circle"></i> My Account
            </h1>

            <Row>
                {/* Left Column - Profile Settings */}
                <Col lg={4} className="mb-4">
                    {/* Update Profile Details Card */}
                    <Card className="shadow-sm mb-3">
                        <Card.Header className="bg-primary text-white">
                            <h5 className="mb-0">
                                <i className="fas fa-user-edit"></i> Update Profile Details
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            {success && (
                                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                                    <i className="fas fa-check-circle"></i> {success}
                                </Alert>
                            )}

                            {error && (
                                <Alert variant="danger" dismissible onClose={() => setError('')}>
                                    <i className="fas fa-exclamation-triangle"></i> {error}
                                </Alert>
                            )}

                            <Form onSubmit={submitHandler}>
                                <Form.Group controlId='name' className='my-2'>
                                    <Form.Label>
                                        <i className="fas fa-user"></i> Name
                                    </Form.Label>
                                    <Form.Control
                                        type='text'
                                        placeholder='Enter name'
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group controlId='email' className='my-2'>
                                    <Form.Label>
                                        <i className="fas fa-envelope"></i> Email Address
                                    </Form.Label>
                                    <Form.Control
                                        type='email'
                                        placeholder='Enter email'
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                </Form.Group>

                                <Button
                                    type='submit'
                                    variant='primary'
                                    className='btn-block blue-outline mt-3'
                                    disabled={loadingUpdateProfile}
                                    style={{ width: '100%' }}
                                >
                                    {loadingUpdateProfile ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i> Updating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save"></i> Save Changes
                                        </>
                                    )}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>

                    {/* Change Password Card */}
                    <Card className="shadow-sm mb-3">
                        <Card.Header className="bg-warning">
                            <h5 className="mb-0">
                                <i className="fas fa-key"></i> Change Password
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Form onSubmit={submitHandler}>
                                <Form.Group controlId='password' className='my-2'>
                                    <Form.Label>
                                        <i className="fas fa-lock"></i> New Password
                                    </Form.Label>
                                    <Form.Control
                                        type='password'
                                        placeholder='Enter new password'
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                </Form.Group>

                                <Form.Group controlId='confirmPassword' className='my-2'>
                                    <Form.Label>
                                        <i className="fas fa-lock"></i> Confirm New Password
                                    </Form.Label>
                                    <Form.Control
                                        type='password'
                                        placeholder='Confirm new password'
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                    />
                                </Form.Group>

                                <Alert variant="info" className="py-2 mt-2 mb-2">
                                    <small>
                                        <i className="fas fa-info-circle"></i> Leave blank to keep your current password
                                    </small>
                                </Alert>

                                <Button
                                    type='submit'
                                    variant='warning'
                                    className='btn-block mt-2'
                                    disabled={loadingUpdateProfile || (!password && !confirmPassword)}
                                    style={{ width: '100%' }}
                                >
                                    {loadingUpdateProfile ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i> Updating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-key"></i> Update Password
                                        </>
                                    )}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>

                    {/* Account Info Card */}
                    <Card className="shadow-sm mt-3">
                        <Card.Header className="bg-light">
                            <h6 className="mb-0">
                                <i className="fas fa-info-circle"></i> Account Information
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <p className="mb-2">
                                <strong>Member Since:</strong>
                                <br />
                                <small className="text-muted">
                                    {new Date(userInfo?.createdAt).toLocaleDateString('en-GB', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </small>
                            </p>
                            <p className="mb-0">
                                <strong>Account Status:</strong>
                                <br />
                                <Badge bg="success">
                                    <i className="fas fa-check-circle"></i> Active
                                </Badge>
                            </p>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Right Column - Order History */}
                <Col lg={8}>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-primary text-white">
                            <h5 className="mb-0">
                                <i className="fas fa-shopping-bag"></i> Order History
                            </h5>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {isLoading ? (
                                <div className="p-4">
                                    <Loader />
                                </div>
                            ) : errorOrders ? (
                                <div className="p-4">
                                    <Message variant='danger'>
                                        {errorOrders?.data?.message || errorOrders.error}
                                    </Message>
                                </div>
                            ) : orders?.length === 0 ? (
                                <div className="text-center p-5">
                                    <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                                    <h5>No Orders Yet</h5>
                                    <p className="text-muted">Start shopping to see your orders here!</p>
                                    <Button
                                        variant="primary"
                                        className="blue-outline"
                                        onClick={() => window.location.href = '/'}
                                    >
                                        <i className="fas fa-shopping-cart"></i> Start Shopping
                                    </Button>
                                </div>
                            ) : (
                                <ListGroup variant="flush">
                                    {orders?.map((order) => (
                                        <ListGroup.Item key={order._id} className="p-3">
                                            <Row className="align-items-center">
                                                {/* Order Number & Date */}
                                                <Col md={3}>
                                                    <div className="mb-1">
                                                        <small className="text-muted">Order #</small>
                                                        <br />
                                                        <strong>{order._id.substring(0, 10)}...</strong>
                                                    </div>
                                                    <div>
                                                        <small className="text-muted">
                                                            <i className="fas fa-calendar"></i>{' '}
                                                            {new Date(order.createdAt).toLocaleDateString('en-GB')}
                                                        </small>
                                                    </div>
                                                </Col>

                                                {/* Total Amount */}
                                                <Col md={2} className="text-center">
                                                    <small className="text-muted d-block">Total</small>
                                                    <strong className="text-primary fs-5">£{order.totalPrice.toFixed(2)}</strong>
                                                </Col>

                                                {/* Payment Status */}
                                                <Col md={2} className="text-center">
                                                    <small className="text-muted d-block mb-1">Payment</small>
                                                    {order.isPaid ? (
                                                        <Badge bg="success">
                                                            <i className="fas fa-check-circle"></i> Paid
                                                        </Badge>
                                                    ) : (
                                                        <Badge bg="danger">
                                                            <i className="fas fa-times-circle"></i> Unpaid
                                                        </Badge>
                                                    )}
                                                </Col>

                                                {/* Delivery Status */}
                                                <Col md={2} className="text-center">
                                                    <small className="text-muted d-block mb-1">Delivery</small>
                                                    {order.isDelivered ? (
                                                        <Badge bg="success">
                                                            <i className="fas fa-truck"></i> Delivered
                                                        </Badge>
                                                    ) : order.cancelledAt ? (
                                                        <Badge bg="danger">
                                                            <i className="fas fa-ban"></i> Cancelled
                                                        </Badge>
                                                    ) : (
                                                        <Badge bg="warning" text="dark">
                                                            <i className="fas fa-box"></i> Processing
                                                        </Badge>
                                                    )}
                                                </Col>

                                                {/* Action Button */}
                                                <Col md={3} className="text-end">
                                                    <LinkContainer to={`/order/${order._id}`}>
                                                        <Button
                                                            variant='outline-primary'
                                                            size="sm"
                                                            className="w-100"
                                                        >
                                                            <i className="fas fa-eye"></i> View Details
                                                        </Button>
                                                    </LinkContainer>
                                                </Col>
                                            </Row>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            )}
                        </Card.Body>

                        {/* Order Summary Footer */}
                        {orders && orders.length > 0 && (
                            <Card.Footer className="bg-light text-center">
                                <small className="text-muted">
                                    <i className="fas fa-info-circle"></i> Total Orders: <strong>{orders.length}</strong>
                                    {' | '}
                                    Total Spent: <strong>£{orders.reduce((acc, order) => acc + order.totalPrice, 0).toFixed(2)}</strong>
                                </small>
                            </Card.Footer>
                        )}
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default ProfileScreen;